import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Phone, Clock3 } from "lucide-react";
import { COMPANY, BLOG_POSTS } from "@/lib/site-data";
import LogoEmblem from "@/components/LogoEmblem";
import { useSeo } from "@/lib/useSeo";

const ease = [0.22, 1, 0.36, 1];

export default function Blog() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSeo({
    title: "Conseils toiture & couverture — Blog NB Couverture, couvreur à Bordeaux",
    description:
      "Conseils de couvreur à Bordeaux : démoussage de toiture, fuite en urgence, choix des matériaux (tuile canal, ardoise, zinc), isolation des combles et aides. Le blog de NB Couverture.",
    canonical: "/conseils",
  });

  return (
    <div className="min-h-screen bg-[#0D0F12] text-slate-200 antialiased">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0D0F12]/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="blog-logo" className="flex items-center gap-3">
            <LogoEmblem size={40} />
            <span className="font-serif text-sm font-semibold text-white">NB Couverture</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="blog-back-home"
              className="hidden items-center gap-2 text-xs font-semibold text-slate-300 transition-colors hover:text-terra sm:inline-flex"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Retour à l'accueil
            </Link>
            <a
              href={COMPANY.phoneHref}
              className="inline-flex items-center gap-2 rounded-full bg-terra px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-terra-hover"
            >
              <Phone className="h-3.5 w-3.5" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-16 md:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-terra">Le blog du couvreur</p>
          <h1 className="mt-5 max-w-2xl font-serif text-4xl font-semibold leading-tight text-white md:text-6xl">
            Conseils toiture à Bordeaux & en Gironde
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
            Démoussage, fuites, matériaux, isolation : nos artisans partagent ce qu'ils voient chaque semaine sur les
            toits de la Gironde.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {BLOG_POSTS.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 2) * 0.08, duration: 0.6, ease }}
            >
              <Link
                to={`/conseils/${p.slug}`}
                data-testid={`blog-list-${p.slug}`}
                className="group flex h-full flex-col border border-white/5 bg-[#14171D] p-7 transition-colors duration-300 hover:border-terra/40"
              >
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  <span className="text-terra">{p.cat}</span>
                  <span>{p.date}</span>
                </div>
                <h2 className="mt-4 flex-1 font-serif text-2xl leading-snug text-slate-100 transition-colors group-hover:text-terra">
                  {p.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.intro}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-400 transition-colors group-hover:text-terra">
                  <Clock3 className="h-3.5 w-3.5" />
                  {p.read} de lecture
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>

      <footer className="border-t border-white/5 py-8">
        <p className="mx-auto max-w-6xl px-6 text-xs text-slate-500">
          {COMPANY.name} — {COMPANY.address} · {COMPANY.phone} · Garantie décennale ·{" "}
          <Link to="/" className="text-slate-400 underline-offset-4 hover:text-terra hover:underline">
            Accueil
          </Link>
        </p>
      </footer>
    </div>
  );
}
